/**
 * قانون‌های اتاقِ منچ (نسخه‌ی ۲) در مرورگر — برچسب‌ها، به‌یادسپاری، بار برای ساختنِ اتاق و قرصِ اتاق.
 *
 * یازده کلید، همان match.GameRules["mench"] ِ سرور و GameSettings/RoomSetupV3 ِ اپ:
 *   start ۰ فقط با ۶ · ۱ سه شانس (پیش‌فرض) · ۲ با ۱ یا ۶ — و safe، pair، bonus، teams، fast،
 *   places، hunter، quick، power هر کدام ۰/۱، و timed ۰ بی‌زمان · ۱ پنج دقیقه · ۲ ده دقیقه.
 *   کلاینتِ تازه **همیشه همه** را می‌فرستد؛ سرور خالی را کلاسیک می‌گیرد (کلاینتِ قدیمی).
 *
 * برچسب‌ها واژه‌به‌واژه همان §2.1 ِ طرح‌اند تا اپ، سایت، اتاق و متنِ قانون یکی بگویند.
 */
export type MenchRules = {
  start: number; safe: number; pair: number; bonus: number; teams: number; fast: number; places: number;
  hunter: number; quick: number; power: number; timed: number;
};

export const MENCH_RULES_KEY = "bazivan.mench.rules";
export const MENCH_RULES_DEFAULT: Readonly<MenchRules> = {
  start: 1, safe: 0, pair: 0, bonus: 0, teams: 0, fast: 0, places: 0, hunter: 0, quick: 0, power: 0, timed: 0,
};
/** سرورِ قدیمی (بی کلیدِ start) = منچِ کلاسیک، نه پیش‌فرضِ تازه. */
export const MENCH_RULES_CLASSIC: Readonly<MenchRules> = {
  start: 0, safe: 0, pair: 0, bonus: 0, teams: 0, fast: 0, places: 0, hunter: 0, quick: 0, power: 0, timed: 0,
};

export type MenchToggle = Exclude<keyof MenchRules, "start" | "timed">;
export const MENCH_TOGGLES: readonly MenchToggle[] = ["safe", "pair", "bonus", "teams", "fast", "places", "quick", "hunter", "power"];

/** ردیفِ «شروع»: سه چیپ. */
export const MENCH_START_LABEL = "شروع";
export const MENCH_START_FA = ["فقط با ۶", "سه شانس", "با ۱ یا ۶"] as const;
/** زیرنویسِ چیپِ انتخاب‌شده (برای «فقط با ۶» چیزی نمی‌گوید). */
export const MENCH_START_SUB = ["", "سه شانس: وقتی مهره‌ای بیرون نداری سه بار تاس می‌ریزی تا شش بیاید", "با ۱ یا ۶: شروع بازی خیلی سریع‌تر می‌شود"] as const;

/** ردیفِ «زمان»: سه چیپ؛ سرِ زمان هر که جلوتر است می‌برد. */
export const MENCH_TIMED_LABEL = "زمان";
export const MENCH_TIMED_FA = ["بی‌زمان", "۵ دقیقه", "۱۰ دقیقه"] as const;
export const MENCH_TIMED_SUB = "وقت که تمام شود، هر که جلوتر است می‌برد: هر خانه‌ی راه یک امتیاز و هر مهره‌ای که زدی پنج امتیاز";

export const MENCH_OPT_FA: Readonly<Record<MenchToggle, string>> = {
  safe: "خانه‌ی امن", pair: "دیوار", bonus: "تاس جایزه", teams: "تیمی ۲ به ۲", fast: "سریع (۲ مهره)", places: "تا نفر آخر",
  quick: "شروع سریع", hunter: "شکارچی", power: "نیروها",
};
export const MENCH_OPT_SUB: Readonly<Record<MenchToggle, string>> = {
  safe: "روی ستاره‌ها و خانه‌های شروع کسی زده نمی‌شود",
  pair: "دو مهره‌ی خودت روی یک خانه: زده نمی‌شوند و کسی از رویشان رد نمی‌شود",
  bonus: "زدن مهره یا رساندنش به خانه یک تاس دیگر می‌دهد",
  teams: "یارت روبه‌روست؛ مهره‌هایت که رسید، برای او تاس می‌ریزی",
  fast: "",
  places: "بعد از نفر اول ادامه دارد تا همه‌ی رتبه‌ها معلوم شود",
  quick: "هر کس از اول یک مهره روی خانه‌ی شروع دارد",
  hunter: "تا مهره‌ی کسی را نزنی، مهره‌هایت وارد راه خانه نمی‌شوند",
  power: "خانه‌های درخشان: سپر، تاس دوبرابر و جابه‌جایی",
};
/** خطِ دومِ «تیمی» در اتاقِ سرور — تعدادِ نشسته‌ها هنوز معلوم نیست. */
export const MENCH_TEAMS_ROOM_SUB = "فقط اگر چهار نفر بنشینند؛ روبه‌روها یارند";

const bit = (v: unknown): number => (v === 1 || v === true ? 1 : 0);

/** از هر شیءِ نیمه‌کاره (localStorage ِ خراب، نسخه‌ی قدیمی) همه‌ی کلیدهای سالم؛ نبودِ start = پیش‌فرض. */
export function menchRulesOf(r: Partial<Record<keyof MenchRules, unknown>> | null | undefined, fallbackStart = 1): MenchRules {
  const start = typeof r?.start === "number" && [0, 1, 2].includes(r.start) ? r.start : fallbackStart;
  const timed = typeof r?.timed === "number" && [0, 1, 2].includes(r.timed) ? r.timed : 0;
  return {
    start, safe: bit(r?.safe), pair: bit(r?.pair), bonus: bit(r?.bonus),
    teams: bit(r?.teams), fast: bit(r?.fast), places: bit(r?.places),
    hunter: bit(r?.hunter), quick: bit(r?.quick), power: bit(r?.power), timed,
  };
}

export function loadMenchRules(): MenchRules {
  try {
    const raw = localStorage.getItem(MENCH_RULES_KEY);
    if (!raw) return { ...MENCH_RULES_DEFAULT };
    return menchRulesOf(JSON.parse(raw) as Partial<MenchRules>);
  } catch { return { ...MENCH_RULES_DEFAULT }; }
}

export function saveMenchRules(r: MenchRules): void {
  try { localStorage.setItem(MENCH_RULES_KEY, JSON.stringify(r)); } catch { /* بی‌حافظه هم کار می‌کند */ }
}

/**
 * بارِ CreateRoom.rules: همه‌ی کلیدها، **همان‌طور که میزبان انتخاب کرده**. نرمال‌سازی فقط یک‌بار
 * و روی سرور است، هنگامِ ساختِ مسابقه — چون فقط آن‌جا شمارِ نشسته‌ها معلوم است: تیمی و «تا نفرِ
 * آخر» هر دو روشن و سه نفر نشستند → بی‌تیم و تا نفرِ آخر؛ چهار نفر → تیمی و بی رتبه‌بندی.
 * پیش از این وب «تا نفرِ آخر» را همین‌جا با تیمی خاموش می‌کرد و اپ نه، و یک انتخاب در دو
 * کلاینت دو بازیِ متفاوت می‌ساخت.
 */
export function menchRoomRules(r: MenchRules): Record<string, number> {
  const x = menchRulesOf(r);
  return {
    start: x.start, safe: x.safe, pair: x.pair, bonus: x.bonus, teams: x.teams, fast: x.fast, places: x.places,
    hunter: x.hunter, quick: x.quick, power: x.power, timed: x.timed,
  };
}

/**
 * قانون‌هایی که با این شمارِ نشسته واقعاً بازی می‌شوند — همان normalized ِ سرور: تیمی فقط با
 * چهار نفر؛ «تا نفرِ آخر» نه در تیمی و نه دو نفره. میزِ کمتر از دو نفر با ربات دو نفره می‌شود.
 */
export function menchEffective(r: MenchRules, seated: number): MenchRules {
  const n = Math.max(2, seated);
  const teams = n === 4 ? r.teams : 0;
  return { ...r, teams, places: teams === 1 || n === 2 ? 0 : r.places };
}

/**
 * قانون‌های اتاق از RoomState.rules. کلیدِ start که نباشد سرور قدیمی است و منچش کلاسیک —
 * همه صفر، هرگز پیش‌فرضِ تازه (سه شانس).
 */
export function menchRulesFromRoom(rules: Readonly<Record<string, number>>): MenchRules {
  if (!("start" in rules)) return { ...MENCH_RULES_CLASSIC };
  return menchRulesOf(rules, 0);
}

/**
 * قرصِ قانون‌ها: فقط آن‌چه با پیش‌فرض فرق دارد، با «، »؛ هیچ = «قانونِ استاندارد».
 * مثلِ «با ۱ یا ۶، دیوار، تیمی ۲ به ۲». نه « · »: نقطه‌ی میانی کنارِ رقمِ فارسی همان «۰» دیده
 * می‌شود و «۶ ·» را «۶۰» می‌خوانند.
 */
export function menchPill(r: MenchRules): string {
  const out: string[] = [];
  if (r.start !== MENCH_RULES_DEFAULT.start) out.push(MENCH_START_FA[r.start] ?? MENCH_START_FA[0]);
  for (const k of MENCH_TOGGLES) if (r[k] === 1) out.push(MENCH_OPT_FA[k]);
  if (r.timed > 0) out.push(MENCH_TIMED_FA[r.timed as 1 | 2]);
  return out.length ? out.join("، ") : "قانون استاندارد";
}
